import { toast } from '@bozic/ui/toast';
import axios, { type AxiosResponse } from 'axios';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getType, type ActionType } from 'typesafe-actions';

import insuranceAPI from '../../services/insuranceService';
import { getOperationName } from '../operationPage/selectors';

import { priceReportActions } from './actions';

import type { OperationPriceReportDto } from '../../types/dto/OperationPreferencesDto';

function* getPriceReport(_action: ActionType<typeof priceReportActions.get>) {
  yield put(priceReportActions.setPriceReportSpinner(true));
  try {
    const operationName = (yield select(getOperationName)) as string;
    const pricereport = (yield call(
      insuranceAPI.getOperationPriceReport,
      operationName
    )) as AxiosResponse<OperationPriceReportDto[]>;
    yield put(priceReportActions.save(pricereport.data));
  } catch (e) {
    console.error(e);
    toast.error('Failed to fetch categories');
  } finally {
    yield put(priceReportActions.setPriceReportSpinner(false));
  }
}

function* createPriceReport(action: ActionType<typeof priceReportActions.add>) {
  yield put(priceReportActions.setPriceReportSpinner(true));
  const operationName = (yield select(getOperationName)) as string;
  try {
    yield call(insuranceAPI.putOperationPriceReport, operationName, action.payload);
    put(priceReportActions.get());
    toast.success('Category saved successfully');
  } catch (error) {
    const responseData: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
    if (typeof responseData === 'string' && responseData) {
      toast.error(responseData);
    } else {
      toast.error('Failed to create category');
    }
  } finally {
    yield call(getPriceReport, priceReportActions.get());
    yield put(priceReportActions.setPriceReportSpinner(false));
    action.meta();
  }
}

function* deletePriceReport(action: ActionType<typeof priceReportActions.delete>) {
  yield put(priceReportActions.setPriceReportSpinner(true));
  const operationName = (yield select(getOperationName)) as string;
  try {
    yield call(insuranceAPI.deleteOperationPriceReport, operationName, action.payload);
    put(priceReportActions.get());
  } catch (e) {
    console.error(e);
    toast.error('Failed to delete preference');
  } finally {
    yield call(getPriceReport, priceReportActions.get());
    yield put(priceReportActions.setPriceReportSpinner(false));
    action.meta();
  }
}

export function* priceReportSagaWatcher() {
  yield all([
    takeLatest(getType(priceReportActions.get), getPriceReport),
    takeLatest(getType(priceReportActions.add), createPriceReport),
    takeLatest(getType(priceReportActions.delete), deletePriceReport)
  ]);
}
